<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, inject, proxyRefs, ref, watch } from 'vue';
import { useImudeiRuntimeModel } from './useImudeiRuntimeModel';

const props = defineProps({
  preview: { type: Boolean, default: false },
  records: { type: Array, default: () => [] },
  metrics: { type: Array, default: () => [] },
  history: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
});

const emit = defineEmits(['navigate']);

const realData = inject('imudeiRealData', null);
const runtimeModel = realData
  ? proxyRefs(
      useImudeiRuntimeModel({
        realData,
        module: computed(() => 'commissions'),
        screen: computed(() => 'dashboard'),
      })
    )
  : null;

const searchQuery = ref('');
const selectedId = ref('');
const statusFilter = ref('all');
const periodFilter = ref('all');
const typeFilter = ref('all');

const asArray = value => (Array.isArray(value) ? value : []);
const sourceOf = record => record?.source || record || {};
const valueAt = (value, path) =>
  path.split('.').reduce((current, key) => current?.[key], value);
const firstValue = (value, paths, fallback = '') => {
  const result = paths
    .map(path => valueAt(value, path))
    .find(item => item !== undefined && item !== null && item !== '');

  return result ?? fallback;
};
const asNumber = value => {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'object') {
    return asNumber(value.amount ?? value.value ?? value.price_amount);
  }

  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};
const money = (value, currency = 'BRL') => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'object') {
    const formatted = firstValue(value, ['formatted', 'display', 'label']);
    if (formatted) return formatted;

    return money(
      value.amount ?? value.amount_cents ?? value.cents,
      value.currency || currency
    );
  }

  const number = asNumber(value);
  if (number === null) return String(value);

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 2,
  }).format(number);
};
const date = value => {
  if (!value) return 'Não informado';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR').format(parsed);
};
const dateTime = value => {
  if (!value) return 'Data não retornada';

  const parsed = new Date(value);
  if (Number.isNaN(parsed.valueOf())) return String(value);

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(parsed);
};
const initial = value =>
  String(value || '—')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toLocaleUpperCase('pt-BR');
const displayLabel = value => {
  if (value === undefined || value === null || value === '') {
    return 'Não retornado';
  }

  return String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, character => character.toLocaleUpperCase('pt-BR'));
};
const mediaUrl = value => {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return mediaUrl(value[0]);

  return firstValue(value, [
    'url',
    'src',
    'secure_url',
    'original_url',
    'file_url',
  ]);
};
const imageFor = source =>
  mediaUrl(
    firstValue(source, [
      'imovel_listing.cover_url',
      'imovel_listing.image_url',
      'imovel_listing.thumbnail',
      'imovel_listing.images',
      'imovel.cover_url',
      'imovel.image_url',
      'property.cover_url',
      'property.image_url',
      'property.images',
      'transaction.imovel.image_url',
      'image_url',
    ])
  );
const statusTone = value => {
  const status = String(value || '').toLocaleLowerCase('pt-BR');
  if (/liberad|conclu|paid|pago/.test(status)) {
    return 'bg-emerald-50 text-emerald-700';
  }
  if (/solicit|pend|review/.test(status)) {
    return 'bg-orange-50 text-orange-700';
  }
  if (/transfer/.test(status)) return 'bg-violet-50 text-violet-700';
  if (/andamento|draft|rascunho/.test(status)) {
    return 'bg-blue-50 text-blue-700';
  }
  return 'bg-slate-50 text-slate-600';
};
const statusGroup = status => {
  const normalized = String(status || '').toLocaleLowerCase('pt-BR');
  if (/liberad/.test(normalized)) return 'released';
  if (/solicit|pend|review/.test(normalized)) return 'requested';
  if (/transfer/.test(normalized)) return 'transferring';
  if (/conclu|paid|pago/.test(normalized)) return 'completed';
  return 'inProgress';
};
const beneficiaryFor = source => {
  const name = firstValue(source, [
    'beneficiary.name',
    'beneficiary_user.name',
    'beneficiary_team.name',
    'broker.name',
    'corretor.name',
    'responsible.name',
  ]);
  if (name) return name;

  const userId = firstValue(source, ['beneficiary_user_id']);
  if (userId) return `Usuário #${userId}`;

  const teamId = firstValue(source, ['beneficiary_team_id']);
  if (teamId) return `Equipe #${teamId}`;

  if (firstValue(source, ['beneficiary_kind']) === 'agency') {
    return 'Imobiliária';
  }

  return 'Beneficiário não retornado';
};

const inputRecords = computed(() => {
  if (props.preview) return [];
  if (props.records.length) return props.records;

  return runtimeModel?.records || [];
});
const hasRuntimeMetrics = computed(() => asArray(props.metrics).length > 0);
const rows = computed(() =>
  inputRecords.value
    .map(record => {
      const source = sourceOf(record);
      const id = String(record.id || source.id || '');
      if (!id) return null;

      const settlementDate = firstValue(source, [
        'expected_settlement_at',
        'settlement_date',
        'due_date',
        'created_at',
      ]);
      const status = firstValue(
        source,
        ['status_label', 'status'],
        record.status
      );
      const saleId = firstValue(source, [
        'sale.code',
        'sale.reference',
        'transaction.code',
        'transaction.reference',
        'sale_id',
        'transaction_id',
      ]);
      const property =
        record.title ||
        firstValue(source, [
          'imovel_listing.title',
          'imovel_listing.titulo',
          'imovel.title',
          'imovel.nome',
          'property.title',
          'property.name',
          'transaction.imovel.title',
        ]) ||
        'Imóvel não retornado';

      return {
        amount: firstValue(
          source,
          ['commission_amount', 'gross_amount', 'amount', 'value'],
          record.amount
        ),
        broker: beneficiaryFor(source),
        brokerCode: firstValue(source, [
          'beneficiary.creci',
          'beneficiary_user.creci',
          'broker.creci',
          'corretor.creci',
        ]),
        code:
          firstValue(source, [
            'code',
            'reference',
            'agreement_code',
            'number',
          ]) || `Comissão #${id}`,
        createdAt: firstValue(source, ['created_at']),
        currency: firstValue(source, ['currency'], 'BRL'),
        id,
        image: imageFor(source),
        location: firstValue(source, [
          'imovel_listing.address.full_address',
          'imovel_listing.endereco_completo',
          'imovel.endereco_completo',
          'property.address.full_address',
          'property.address',
          'transaction.imovel.endereco_completo',
        ]),
        netAmount: firstValue(source, [
          'net_amount',
          'settlement_amount',
          'amount_net',
          'net_value',
        ]),
        property,
        sale: saleId ? String(saleId) : 'Venda não retornada',
        settlementDate,
        source,
        status: displayLabel(status),
        statusKey: statusGroup(status),
        type: firstValue(source, ['commission_kind_label', 'commission_kind']),
      };
    })
    .filter(Boolean)
);
const statusOptions = computed(() =>
  [...new Set(rows.value.map(row => row.status).filter(Boolean))].sort()
);
const typeOptions = computed(() =>
  [...new Set(rows.value.map(row => row.type).filter(Boolean))].sort()
);
const metricValueFor = label => {
  const metric = asArray(props.metrics).find(
    item => displayLabel(firstValue(item, ['label', 'name'])) === label
  );

  return firstValue(metric, ['value', 'amount', 'total']);
};
const summaryValue = (amounts, total, metricValue) => {
  if (amounts.length > 0) return money(total);
  if (metricValue === '') return '—';

  return String(metricValue);
};
const matchesPeriod = row => {
  if (periodFilter.value === 'all') return true;

  const rowDate = new Date(row.settlementDate || row.createdAt);
  if (Number.isNaN(rowDate.valueOf())) return false;

  const today = new Date();
  if (periodFilter.value === 'month') {
    return (
      rowDate.getMonth() === today.getMonth() &&
      rowDate.getFullYear() === today.getFullYear()
    );
  }

  const limit = new Date(today);
  limit.setDate(today.getDate() - 90);
  return rowDate >= limit && rowDate <= today;
};
const filteredRows = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase('pt-BR');

  return rows.value.filter(row => {
    const searchText = [
      row.code,
      row.property,
      row.location,
      row.sale,
      row.broker,
      row.status,
    ]
      .filter(Boolean)
      .join(' ')
      .toLocaleLowerCase('pt-BR');
    const matchesSearch = !query || searchText.includes(query);
    const matchesStatus =
      statusFilter.value === 'all' || row.status === statusFilter.value;
    const matchesType =
      typeFilter.value === 'all' || row.type === typeFilter.value;

    return matchesSearch && matchesStatus && matchesType && matchesPeriod(row);
  });
});
const summaryCards = computed(() => {
  const cardConfig = [
    ['inProgress', 'Em andamento', 'i-lucide-clock-3', 'text-blue-700'],
    ['released', 'Liberadas', 'i-lucide-check', 'text-emerald-700'],
    ['requested', 'Solicitadas', 'i-lucide-file-text', 'text-orange-700'],
    ['transferring', 'Em transferência', 'i-lucide-send', 'text-violet-700'],
    ['completed', 'Concluídas', 'i-lucide-flag', 'text-emerald-700'],
  ];

  return cardConfig.map(([id, label, icon, iconTone]) => {
    const matchingRows = rows.value.filter(row => row.statusKey === id);
    const amounts = matchingRows
      .map(row => asNumber(row.amount))
      .filter(amount => amount !== null);
    const total = amounts.reduce((sum, amount) => sum + amount, 0);
    const metricValue = metricValueFor(label);

    return {
      count: matchingRows.length,
      icon,
      iconTone,
      id,
      label,
      value: summaryValue(amounts, total, metricValue),
    };
  });
});
const selected = computed(
  () => rows.value.find(row => row.id === selectedId.value) || null
);
const reconciliation = computed(() =>
  asArray(realData?.data?.value?.reconciliation)
);
const selectedPool = computed(() => {
  if (!selected.value) return null;

  return (
    reconciliation.value.find(
      pool => String(pool?.agreement?.id) === selected.value.id
    ) || null
  );
});
const participants = computed(() =>
  asArray(selectedPool.value?.splits).map((split, index) => ({
    amount: firstValue(split, ['amount', 'net_amount']),
    id: String(split.id || split.beneficiary_id || index),
    name: firstValue(split, [
      'beneficiary_label',
      'beneficiary.name',
      'beneficiary_name',
    ]),
    percentage: firstValue(split, ['percentage', 'percent']),
    role: firstValue(split, ['role', 'beneficiary_kind']),
    status: firstValue(split, ['status']),
  }))
);
const documentItems = computed(() => {
  if (!selected.value) return [];

  const poolDocuments = firstValue(selectedPool.value, [
    'documents',
    'required_documents',
    'attachments',
  ]);
  const documents =
    poolDocuments ||
    firstValue(
      selected.value.source,
      ['documents', 'required_documents', 'attachments'],
      []
    );

  return asArray(documents).map((document, index) => ({
    id: String(document.id || document.key || index),
    label: firstValue(document, ['name', 'label', 'title', 'document_type']),
    status: firstValue(document, ['status', 'state']),
  }));
});
const linkedLedger = computed(() => {
  if (!selected.value) return [];

  const poolEntries = asArray(selectedPool.value?.ledger_entries);
  if (poolEntries.length) return poolEntries;

  return asArray(realData?.data?.value?.ledger).filter(entry => {
    const agreementId = firstValue(entry, [
      'agreement_id',
      'commission_agreement_id',
    ]);

    return agreementId && String(agreementId) === selected.value.id;
  });
});
const linkedHistory = computed(() => {
  if (!selected.value) return [];

  return asArray(props.history).filter(event => {
    const agreementId = firstValue(sourceOf(event), [
      'agreement_id',
      'commission_agreement_id',
      'agreement.id',
    ]);

    return agreementId && String(agreementId) === selected.value.id;
  });
});
const auditItems = computed(() =>
  [...linkedLedger.value, ...linkedHistory.value]
    .slice(0, 3)
    .map((entry, index) => ({
      date: firstValue(entry, [
        'occurred_at',
        'created_at',
        'posted_at',
        'updated_at',
      ]),
      detail: firstValue(entry, [
        'description',
        'reason',
        'status',
        'entry_type',
        'kind',
      ]),
      id: String(entry.id || entry.entry_id || index),
      label: firstValue(entry, ['label', 'title', 'event', 'action', 'type']),
    }))
);
const canEditSelected = computed(() => {
  if (!selected.value) return false;

  return Boolean(
    firstValue(selected.value.source, [
      'permissions.can_update',
      'permissions.can_manage',
      'can_update',
      'can_manage',
    ])
  );
});
const integrityCopy = computed(() => {
  if (!selected.value) {
    return 'Selecione uma comissão para consultar a conciliação vinculada.';
  }
  if (!selectedPool.value) {
    return 'O backend não retornou uma conciliação vinculada a este acordo.';
  }
  if (selectedPool.value.balanced === true) {
    return 'Rateio conciliado pelo backend para este acordo.';
  }
  if (selectedPool.value.balanced === false) {
    const issues = asArray(selectedPool.value.issues)
      .map(issue => String(issue))
      .filter(Boolean)
      .join(' · ');

    return issues || 'O backend sinalizou pendências nesta conciliação.';
  }

  return 'A conciliação vinculada não informou o estado de integridade.';
});

const open = screen => {
  if (!selected.value?.id) return;

  emit('navigate', { id: selected.value.id, screen });
};
const selectRow = id => {
  selectedId.value = id;
};

watch(
  filteredRows,
  availableRows => {
    if (
      !selectedId.value ||
      !availableRows.some(row => row.id === selectedId.value)
    ) {
      selectedId.value = availableRows[0]?.id || '';
    }
  },
  { immediate: true }
);
</script>

<template>
  <main class="min-h-full bg-[#fbfaff] px-5 pb-6 pt-0 text-[#16266d] lg:px-8">
    <header
      class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between"
    >
      <div>
        <h1 class="text-3xl font-semibold tracking-tight">Comissões</h1>
        <p class="sr-only">
          Acompanhe acordos, rateios e a trilha auditável da operação.
        </p>
      </div>
      <p
        v-if="preview && !hasRuntimeMetrics"
        class="rounded-lg bg-violet-50 px-3 py-2 text-xs text-violet-700"
      >
        Dados de runtime indisponíveis na pré-visualização.
      </p>
    </header>

    <section class="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <article
        v-for="card in summaryCards"
        :key="card.id"
        class="rounded-2xl border border-[#ece8f2] bg-white px-4 py-4 shadow-sm"
      >
        <div class="flex items-center gap-3">
          <span
            class="grid size-10 place-items-center rounded-full bg-[#f4f0ff]"
            :class="card.iconTone"
          >
            <i class="size-5" :class="card.icon" />
          </span>
          <span>
            <small class="block text-[11px] text-[#536286]">{{
              card.label
            }}</small>
            <strong class="mt-1 block text-lg text-[#16266d]">{{
              card.value
            }}</strong>
          </span>
        </div>
        <small class="mt-3 block text-[11px] text-[#536286]">
          {{ card.count }} {{ card.count === 1 ? 'comissão' : 'comissões' }}
        </small>
      </article>
    </section>

    <section class="mt-5 grid gap-5 2xl:grid-cols-[minmax(0,1fr)_360px]">
      <div class="min-w-0">
        <section
          class="rounded-2xl border border-[#ece8f2] bg-white p-3 shadow-sm"
        >
          <div
            class="grid gap-2 lg:grid-cols-[minmax(0,1.8fr)_repeat(3,minmax(0,0.78fr))]"
          >
            <label class="relative block">
              <i
                class="i-lucide-search absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#536286]"
              />
              <input
                v-model="searchQuery"
                class="h-10 w-full rounded-lg border border-[#e8e3f0] bg-white pl-10 pr-3 text-xs text-[#16266d] outline-none ring-violet-200 transition focus:ring-2"
                placeholder="Buscar por código, imóvel, venda ou beneficiário"
                type="search"
              />
            </label>
            <select
              v-model="statusFilter"
              class="h-10 rounded-lg border border-[#e8e3f0] bg-white px-3 text-xs text-[#16266d] outline-none"
            >
              <option value="all">Status: Todos</option>
              <option
                v-for="status in statusOptions"
                :key="status"
                :value="status"
              >
                Status: {{ status }}
              </option>
            </select>
            <select
              v-model="periodFilter"
              class="h-10 rounded-lg border border-[#e8e3f0] bg-white px-3 text-xs text-[#16266d] outline-none"
            >
              <option value="all">Período: Todos</option>
              <option value="month">Período: Este mês</option>
              <option value="last90">Período: Últimos 90 dias</option>
            </select>
            <select
              v-model="typeFilter"
              class="h-10 rounded-lg border border-[#e8e3f0] bg-white px-3 text-xs text-[#16266d] outline-none"
            >
              <option value="all">Tipo: Todos</option>
              <option v-for="type in typeOptions" :key="type" :value="type">
                Tipo: {{ displayLabel(type) }}
              </option>
            </select>
          </div>
        </section>

        <section
          class="mt-4 overflow-hidden rounded-2xl border border-[#ece8f2] bg-white shadow-sm"
        >
          <div class="overflow-x-auto">
            <table class="w-full min-w-[920px] border-collapse text-left">
              <thead
                class="border-b border-[#ece8f2] text-[11px] text-[#16266d]"
              >
                <tr>
                  <th class="px-3 py-3 font-semibold">Comissão</th>
                  <th class="px-3 py-3 font-semibold">Imóvel</th>
                  <th class="px-3 py-3 font-semibold">Venda</th>
                  <th class="px-3 py-3 font-semibold">Beneficiário</th>
                  <th class="px-3 py-3 font-semibold">Previsto</th>
                  <th class="px-3 py-3 font-semibold">Valor</th>
                  <th class="px-3 py-3 font-semibold">Status</th>
                  <th class="px-3 py-3">
                    <span class="sr-only">Visualizar</span>
                  </th>
                </tr>
              </thead>
              <tbody v-if="filteredRows.length">
                <tr
                  v-for="row in filteredRows"
                  :key="row.id"
                  class="border-b border-[#f0edf5] last:border-b-0 hover:bg-[#fcfbff]"
                  :class="{ 'bg-violet-50/40': selectedId === row.id }"
                >
                  <td class="px-3 py-3 align-top">
                    <button
                      class="text-left"
                      type="button"
                      @click="selectRow(row.id)"
                    >
                      <strong class="block text-xs text-[#3c12b8]">{{
                        row.code
                      }}</strong>
                      <small class="mt-1 block text-[10px] text-[#67718f]">
                        Criada em {{ date(row.createdAt) }}
                      </small>
                    </button>
                  </td>
                  <td class="px-3 py-3 align-top">
                    <button
                      class="flex max-w-[190px] items-center gap-2 text-left"
                      type="button"
                      @click="selectRow(row.id)"
                    >
                      <img
                        v-if="row.image"
                        :alt="row.property"
                        :src="row.image"
                        class="size-11 rounded-lg object-cover"
                      />
                      <span
                        v-else
                        class="grid size-11 place-items-center rounded-lg bg-violet-50 text-[#5a24c7]"
                      >
                        <i class="i-lucide-building-2 size-5" />
                      </span>
                      <span class="min-w-0">
                        <strong
                          class="block truncate text-[11px] text-[#3c12b8]"
                          >{{ row.property }}</strong
                        >
                        <small
                          class="mt-1 block truncate text-[10px] text-[#67718f]"
                        >
                          {{ row.location || 'Endereço não retornado' }}
                        </small>
                      </span>
                    </button>
                  </td>
                  <td class="px-3 py-3 align-top">
                    <strong class="block text-[11px] text-[#3c12b8]">{{
                      row.sale
                    }}</strong>
                    <small class="mt-1 block text-[10px] text-[#67718f]">{{
                      date(row.createdAt)
                    }}</small>
                  </td>
                  <td class="px-3 py-3 align-top">
                    <strong class="block text-[11px] text-[#16266d]">{{
                      row.broker
                    }}</strong>
                    <small class="mt-1 block text-[10px] text-[#67718f]">
                      {{ row.brokerCode || 'Registro não retornado' }}
                    </small>
                  </td>
                  <td class="px-3 py-3 align-top text-[11px] text-[#3e4c77]">
                    {{ date(row.settlementDate) }}
                  </td>
                  <td class="px-3 py-3 align-top">
                    <strong class="block text-[11px] text-[#16266d]">
                      {{ money(row.amount, row.currency) }}
                    </strong>
                    <small class="mt-1 block text-[10px] text-[#536286]">
                      Líquido:
                      {{
                        row.netAmount
                          ? money(row.netAmount, row.currency)
                          : 'Não retornado'
                      }}
                    </small>
                  </td>
                  <td class="px-3 py-3 align-top">
                    <span
                      class="rounded-md px-2 py-1 text-[10px]"
                      :class="statusTone(row.status)"
                    >
                      {{ row.status }}
                    </span>
                  </td>
                  <td class="px-3 py-3 align-top">
                    <button
                      class="grid size-8 place-items-center rounded-lg border border-[#e8e3f0] text-[#3c12b8]"
                      type="button"
                      aria-label="Visualizar comissão"
                      @click="selectRow(row.id)"
                    >
                      <i class="i-lucide-eye size-4" />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="!filteredRows.length"
            class="grid min-h-80 place-items-center px-5 text-center"
          >
            <span>
              <i
                class="i-lucide-chart-no-axes-combined mx-auto size-8 text-[#a58add]"
              />
              <strong class="mt-3 block text-sm"
                >Nenhuma comissão encontrada</strong
              >
              <small class="mt-1 block text-xs text-[#7480a0]">
                A conta atual não retornou acordos compatíveis com estes
                filtros.
              </small>
            </span>
          </div>
          <footer
            class="flex items-center justify-between gap-3 border-t border-[#ece8f2] px-4 py-3 text-[10px] text-[#67718f]"
          >
            <span
              >Mostrando {{ filteredRows.length }} de
              {{ total || rows.length }} comissões</span
            >
            <span class="rounded-md bg-[#f5f1ff] px-2 py-1 text-[#5a24c7]"
              >Dados em tempo real</span
            >
          </footer>
        </section>
      </div>

      <aside
        class="min-h-[720px] rounded-2xl border border-[#e8e4ef] bg-white p-4 shadow-sm 2xl:-mt-[185px] 2xl:flex 2xl:min-h-[990px] 2xl:flex-col"
      >
        <template v-if="selected">
          <header class="flex items-center justify-between gap-3">
            <h2 class="text-[11px] font-bold">
              VISUALIZAÇÃO RÁPIDA DA COMISSÃO
            </h2>
            <button
              class="grid size-7 place-items-center rounded-md text-[#5d6687] hover:bg-slate-50"
              type="button"
              aria-label="Fechar visualização"
              @click="selectedId = ''"
            >
              <i class="i-lucide-x size-4" />
            </button>
          </header>

          <div class="mt-3 flex items-start justify-between gap-3">
            <span class="min-w-0">
              <strong
                class="inline-block rounded bg-[#4c1dbb] px-3 py-1.5 text-[10px] text-white"
              >
                {{ selected.code }}
              </strong>
            </span>
            <span
              class="shrink-0 rounded-md px-2 py-1 text-[10px]"
              :class="statusTone(selected.status)"
            >
              {{ selected.status }}
            </span>
          </div>

          <div class="mt-2 flex items-center gap-3">
            <img
              v-if="selected.image"
              :alt="selected.property"
              :src="selected.image"
              class="size-16 rounded-xl object-cover"
            />
            <span
              v-else
              class="grid size-16 place-items-center rounded-xl bg-violet-50 text-[#5a24c7]"
            >
              <i class="i-lucide-building-2 size-5" />
            </span>
            <span class="min-w-0">
              <h3 class="truncate text-sm font-bold">
                {{ selected.property }}
              </h3>
              <small class="mt-1 block truncate text-[10px] text-[#7480a0]">
                {{ selected.sale }}
              </small>
              <small class="mt-1 block truncate text-[10px] text-[#7480a0]">
                {{ selected.location || `Beneficiário: ${selected.broker}` }}
              </small>
            </span>
          </div>

          <div
            class="mt-3 grid grid-cols-2 gap-3 border-y border-[#eeeaf4] py-3"
          >
            <span>
              <small class="block text-[10px] text-[#7480a0]"
                >Total bruto</small
              >
              <strong class="mt-2 block text-base">{{
                money(selected.amount, selected.currency)
              }}</strong>
            </span>
            <span class="border-l border-[#eeeaf4] pl-3">
              <small class="block text-[10px] text-[#7480a0]"
                >Total líquido</small
              >
              <strong class="mt-2 block text-base text-emerald-600">
                {{
                  selected.netAmount
                    ? money(selected.netAmount, selected.currency)
                    : 'Não retornado'
                }}
              </strong>
            </span>
          </div>

          <section class="mt-3 min-h-[150px]">
            <h3 class="text-[11px] font-bold">
              Participantes do rateio ({{ participants.length }})
            </h3>
            <div
              v-for="participant in participants"
              :key="participant.id"
              class="mt-3 flex items-center gap-2"
            >
              <span
                class="grid size-8 place-items-center rounded-full bg-violet-100 text-[9px] font-bold text-[#5a24c7]"
              >
                {{ initial(participant.name) }}
              </span>
              <span class="min-w-0 flex-1">
                <strong class="block truncate text-[10px]">{{
                  participant.name || 'Beneficiário não retornado'
                }}</strong>
                <small class="block text-[9px] text-[#7480a0]">
                  {{ displayLabel(participant.role) }}
                </small>
              </span>
              <span class="text-right">
                <strong class="block text-[10px]">{{
                  participant.percentage || '—'
                }}</strong>
                <small class="block text-[9px] text-[#7480a0]">
                  {{
                    participant.amount
                      ? money(participant.amount, selected.currency)
                      : '—'
                  }}
                </small>
              </span>
            </div>
            <p
              v-if="!participants.length"
              class="mt-3 text-[10px] leading-4 text-[#7480a0]"
            >
              O backend não retornou rateio vinculado a este acordo.
            </p>
          </section>

          <section class="mt-5 min-h-[126px] border-t border-[#eeeaf4] pt-4">
            <h3 class="text-[11px] font-bold">Documentação necessária</h3>
            <div
              v-for="document in documentItems.slice(0, 3)"
              :key="document.id"
              class="mt-3 flex items-center gap-2"
            >
              <i class="i-lucide-file-text size-4 text-[#5a24c7]" />
              <span class="min-w-0 flex-1 truncate text-[10px]">{{
                document.label || 'Documento sem nome'
              }}</span>
              <span
                class="rounded px-2 py-1 text-[9px]"
                :class="statusTone(document.status)"
              >
                {{ displayLabel(document.status) }}
              </span>
            </div>
            <p
              v-if="!documentItems.length"
              class="mt-3 text-[10px] leading-4 text-[#7480a0]"
            >
              A API atual não retornou documentos deste acordo.
            </p>
          </section>

          <section class="mt-5 min-h-[150px] border-t border-[#eeeaf4] pt-4">
            <h3 class="text-[11px] font-bold">Atividades auditáveis</h3>
            <div
              v-for="item in auditItems"
              :key="item.id"
              class="mt-3 flex gap-2"
            >
              <i class="i-lucide-history size-4 text-[#5a24c7]" />
              <span class="min-w-0 flex-1">
                <strong class="block text-[10px]">{{
                  item.label || 'Evento auditável'
                }}</strong>
                <small class="block truncate text-[9px] text-[#7480a0]">
                  {{ item.detail || 'Detalhe não retornado' }} ·
                  {{ dateTime(item.date) }}
                </small>
              </span>
            </div>
            <p
              v-if="!auditItems.length"
              class="mt-3 text-[10px] leading-4 text-[#7480a0]"
            >
              Não há lançamentos vinculados a este acordo no retorno atual.
            </p>
          </section>

          <footer class="mt-6 grid grid-cols-2 gap-2 2xl:mt-auto">
            <button
              class="h-9 rounded-lg border border-[#d7c8ff] text-[10px] font-bold text-[#5a24c7]"
              type="button"
              @click="open('detail')"
            >
              Abrir detalhes
            </button>
            <button
              class="h-9 rounded-lg border border-[#d7c8ff] text-[10px] font-bold text-[#5a24c7] disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400"
              type="button"
              :disabled="!canEditSelected"
              @click="open('edit')"
            >
              Editar rateio
            </button>
          </footer>
        </template>

        <div
          v-else
          class="grid min-h-[580px] place-items-center px-5 text-center"
        >
          <span>
            <i
              class="i-lucide-panel-right-open mx-auto size-8 text-[#a58add]"
            />
            <strong class="mt-3 block text-sm">Selecione uma comissão</strong>
            <small class="mt-1 block text-xs text-[#7480a0]">
              O painel exibe apenas dados vinculados ao acordo selecionado.
            </small>
          </span>
        </div>

        <article
          class="mt-4 rounded-xl border border-violet-200 bg-violet-50 p-3"
        >
          <div class="flex items-center gap-2">
            <i class="i-lucide-shield-check size-4 text-[#5a24c7]" />
            <h3 class="text-[11px] font-bold">Integridade auditável</h3>
          </div>
          <p class="mt-2 text-[10px] leading-4 text-[#65708f]">
            {{ integrityCopy }}
          </p>
        </article>

        <article class="mt-3 rounded-xl border border-[#ece8f2] bg-white p-3">
          <div class="flex items-center gap-2">
            <i class="i-lucide-medal size-4 text-[#5a24c7]" />
            <h3 class="text-[11px] font-bold">Gamificação</h3>
          </div>
          <p class="mt-2 text-[10px] leading-4 text-[#65708f]">
            Pontos e missões não foram retornados pela API desta conta.
          </p>
        </article>
      </aside>
    </section>
  </main>
</template>
